import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserRound,
  PackageCheck,
  Heart,
  Lock,
  LogOut,
  Edit3,
  Save,
} from "lucide-react";
import { ECWID_STORE_ID, ECWID_ACCESS_TOKEN } from "../config/EcwidConfig";

const MyProfilePage = () => {
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState({
    id: "",
    name: "",
    email: "",
    phone: "",
    dob: "",
    gender: "male",
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  //  Fetch profile from Ecwid
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const stored = localStorage.getItem("customerProfile");
        if (stored) {
          const customer = JSON.parse(stored);
          setProfileData({
            id: customer.id,
            name: customer.billingPerson?.name || "",
            email: customer.email || "",
            phone: customer.billingPerson?.phone || "",
            dob: customer.dob || "",
            gender: customer.gender || "male",
          });
          setLoading(false);
          return;
        }

        // Example: Get current logged-in customer (replace {id} dynamically if you store it at SignIn)
        const response = await fetch(
          `https://app.ecwid.com/api/v3/${ECWID_STORE_ID}/customers/{customerId}`,
          {
            headers: {
              Authorization: `Bearer ${ECWID_ACCESS_TOKEN}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch profile");

        const data = await response.json();
        const formatted = {
          id: data.id,
          name: data.billingPerson?.name || "",
          email: data.email || "",
          phone: data.billingPerson?.phone || "",
          dob: data.dob || "",
          gender: data.gender || "male",
        };
        setProfileData(formatted);
        localStorage.setItem("customerProfile", JSON.stringify(data));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleLogout = () => {
    // Clear all user-related data from localStorage
    localStorage.removeItem("customer");
    localStorage.removeItem("customerProfile");
    localStorage.removeItem("customerEmail");

    // Also clear any stored password
    // We need to check if there's a customerEmail stored to remove the corresponding password
    const storedEmail = localStorage.getItem("customerEmail");
    if (storedEmail) {
      localStorage.removeItem(`pw_${storedEmail}`);
    }

    // Close dropdown
    // setDropdownOpen(false);

    // Navigate to sign in page
    navigate("/SignIn");
  };

  //  Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (passwordError) setPasswordError("");
  };

  //  Save profile (Ecwid + localStorage)
  const handleSave = async () => {
    try {
      const res = await fetch(
        `https://app.ecwid.com/api/v3/${ECWID_STORE_ID}/customers/${profileData.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${ECWID_ACCESS_TOKEN}`,
          },
          body: JSON.stringify({
            email: profileData.email,
            dob: profileData.dob,
            gender: profileData.gender,
            billingPerson: {
              name: profileData.name,
              phone: profileData.phone,
            },
          }),
        }
      );
      if (!res.ok) throw new Error("Failed to update profile");

      const updated = await res.json();
      localStorage.setItem("customerProfile", JSON.stringify(updated));
      alert("Profile updated successfully!");
      setEditMode(false);
    } catch (err) {
      console.error(err);
      alert("Error updating profile!");
    }
  };

  const handleChangePassword = async () => {
    // Validate passwords
    if (
      !passwordData.currentPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      setPasswordError("All fields are required");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("New password and confirm password do not match");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }

    try {
      const res = await fetch(
        `https://app.ecwid.com/api/v3/${ECWID_STORE_ID}/customers/${profileData.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${ECWID_ACCESS_TOKEN}`,
          },
          body: JSON.stringify({
            password: passwordData.newPassword,
            oldPassword: passwordData.currentPassword,
          }),
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to update password");
      }

      const updated = await res.json();
      localStorage.setItem("customerProfile", JSON.stringify(updated));
      const storedEmail = localStorage.getItem("customerEmail");
      if (storedEmail) {
        localStorage.setItem(`pw_${storedEmail}`, passwordData.newPassword);
      }
      setPasswordSuccess("Password updated successfully!");
      setPasswordError("");

      // Clear form
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess("");
      }, 2000);
    } catch (err) {
      console.error(err);
      setPasswordError(err.message || "Error updating password!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#82b016] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      <div className="max-w-100 mx-auto">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="lg:min-h-screen bg-gradient-to-l from-gray-300 to-gray-100 p-6 sticky top-6">
              <div className="flex flex-col items-center mb-8">
                <div className="w-20 h-20 bg-gray-400 rounded-full flex items-center justify-center mb-4">
                  <UserRound className="text-white w-10 h-10" />
                </div>
                <h2 className="text-xl font-bold text-gray-800 capitalize">
                  {profileData.name || "User"}
                </h2>
                <p className="text-gray-600 text-sm">
                  {profileData.email || "No email provided"}
                </p>
              </div>

              <nav className="space-y-2">
                <SidebarItem icon={<UserRound />} text="My Account" active />
                <SidebarItem
                  icon={<PackageCheck className="text-gray-500" />}
                  text="My Orders"
                  onClick={() => navigate("/YourOrders")}
                />
                <SidebarItem
                  icon={<Heart className="text-gray-500" />}
                  text="My Cart"
                  onClick={() => navigate("/cart")}
                />
                <SidebarItem
                  icon={<Lock className="text-gray-500" />}
                  onClick={() => setShowPasswordModal(true)}
                  text="Change Password"
                />
                <SidebarItem
                  icon={<LogOut className="text-red-500" />}
                  text="Logout"
                  onClick={handleLogout}
                />
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="px-5 lg:px-0 py-6">
              <div className="bg-gradient-to-r from-[#82b016] to-green-600 text-white rounded-xl shadow-lg py-3 px-4 mb-8 border border-gray-100">
                <h1 className="text-2xl md:text-3xl font-bold mb-2">
                  My Profile
                </h1>
                <p>Manage your account information and preferences</p>
              </div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    Personal Information
                  </h2>
                  <p className="text-gray-600">
                    Update your personal details here
                  </p>
                </div>
                {!editMode ? (
                  <button
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#82b016] to-green-600 hover:from-[#6fa010] hover:to-green-700 text-white font-medium rounded-lg shadow-md transition-all duration-300 transform hover:-translate-y-0.5"
                    onClick={() => setEditMode(true)}
                  >
                    <Edit3 size={18} /> Edit Profile
                  </button>
                ) : (
                  <button
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#82b016] to-green-600 hover:from-[#6fa010] hover:to-green-700 text-white font-medium rounded-lg transition-all duration-300 shadow hover:shadow-md"
                    onClick={handleSave}
                  >
                    <Save size={18} /> Save Changes
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  label="Full Name"
                  name="name"
                  value={profileData.name}
                  onChange={handleChange}
                  readOnly={!editMode}
                />
                <InputField
                  label="Email Address"
                  name="email"
                  type="email"
                  value={profileData.email}
                  onChange={handleChange}
                  readOnly={!editMode}
                />
                <InputField
                  label="Phone Number"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleChange}
                  readOnly={!editMode}
                />
                <InputField
                  label="Date of Birth"
                  name="dob"
                  type="date"
                  value={profileData.dob}
                  onChange={handleChange}
                  readOnly={!editMode}
                />
              </div>

              <div className="mt-6">
                <GenderField
                  gender={profileData.gender}
                  onChange={handleChange}
                  editMode={editMode}
                />
              </div>

              {editMode && (
                <div className="mt-8 flex justify-end">
                  <button
                    className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium transition mr-3"
                    onClick={() => setEditMode(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-6 py-2 bg-gradient-to-r from-[#82b016] to-green-600 hover:from-[#6fa010] hover:to-green-700 text-white font-medium rounded-lg transition-all duration-300 shadow hover:shadow-md"
                    onClick={handleSave}
                  >
                    Save Changes
                  </button>
                </div>
              )}
            </div>

            {/* Additional Sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 px-5 lg:px-0">
              <div className="bg-gray-100 p-6 border border-gray-300 rounded-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  Account Security
                </h3>
                <p className="text-gray-600 mb-4">
                  Manage your password and security settings
                </p>
                <button
                  className="flex items-center gap-2 text-[#82b016] hover:text-[#6fa010] font-medium"
                  onClick={() => setShowPasswordModal(true)}
                >
                  <Lock size={18} /> Change Password
                </button>
              </div>

              <div className="bg-gray-100 p-6 border border-gray-300 rounded-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  Preferences
                </h3>
                <p className="text-gray-600 mb-4">
                  Manage your notification and communication preferences
                </p>
                <button className="flex items-center gap-2 text-[#82b016] hover:text-[#6fa010] font-medium">
                  <Edit3 size={18} /> Update Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">
                  Change Password
                </h3>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordError("");
                    setPasswordSuccess("");
                    setPasswordData({
                      currentPassword: "",
                      newPassword: "",
                      confirmPassword: "",
                    });
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {passwordSuccess ? (
                <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">
                  {passwordSuccess}
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    <InputField
                      label="Current Password"
                      name="currentPassword"
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      placeholder="Enter current password"
                    />
                    <InputField
                      label="New Password"
                      name="newPassword"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      placeholder="Enter new password"
                    />
                    <InputField
                      label="Confirm New Password"
                      name="confirmPassword"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      placeholder="Confirm new password"
                    />
                  </div>

                  {passwordError && (
                    <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-lg">
                      {passwordError}
                    </div>
                  )}

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition"
                      onClick={() => {
                        setShowPasswordModal(false);
                        setPasswordError("");
                        setPasswordSuccess("");
                        setPasswordData({
                          currentPassword: "",
                          newPassword: "",
                          confirmPassword: "",
                        });
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 bg-gradient-to-r from-[#82b016] to-green-600 hover:from-[#6fa010] hover:to-green-700 text-white font-medium rounded-lg transition-all duration-300 shadow hover:shadow-md"
                      onClick={handleChangePassword}
                    >
                      Update Password
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sidebar item
const SidebarItem = ({ icon, text, active, onClick }) => (
  <div
    className={`flex items-center px-4 py-3 rounded-lg cursor-pointer transition-colors ${
      active
        ? "bg-gradient-to-r from-[#82b016] to-green-600 hover:from-[#6fa010] hover:to-green-700 text-white font-medium rounded-lg shadow-md transition-all duration-300"
        : "hover:bg-white hover:text-gray-800 text-gray-800"
    }`}
    onClick={onClick}
  >
    <span className="w-5 h-5 mr-3">{icon}</span>
    <span>{text}</span>
  </div>
);

// Input field
const InputField = ({
  label,
  name,
  value,
  onChange,
  readOnly,
  type = "text",
  placeholder,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      placeholder={placeholder}
      className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#82b016] focus:border-[#82b016] transition-colors ${
        readOnly ? "bg-gray-50 text-gray-600 cursor-not-allowed" : "bg-white"
      }`}
    />
  </div>
);

// Gender field
const GenderField = ({ gender, onChange, editMode }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Gender
    </label>
    <div className="flex items-center space-x-6">
      <label className="flex items-center">
        <input
          type="radio"
          name="gender"
          value="male"
          checked={gender === "male"}
          onChange={onChange}
          disabled={!editMode}
          className="mr-2 h-5 w-5 text-[#82b016] focus:ring-[#82b016]"
        />
        <span className={editMode ? "text-gray-700" : "text-gray-500"}>
          Male
        </span>
      </label>
      <label className="flex items-center">
        <input
          type="radio"
          name="gender"
          value="female"
          checked={gender === "female"}
          onChange={onChange}
          disabled={!editMode}
          className="mr-2 h-5 w-5 text-[#82b016] focus:ring-[#82b016]"
        />
        <span className={editMode ? "text-gray-700" : "text-gray-500"}>
          Female
        </span>
      </label>
    </div>
  </div>
);

export default MyProfilePage;
